import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { AuthService, ContentService, ContextService, FeaturesService } from '@core/services';
import { ChangableComponent } from '@models/changable.component';
import { ErrorNotificationService } from '@shared/error-notification/error-notification.service';
import { FeatureEnum, IContentStats, IContentVisitInfo, LikeDislikeEnum } from 'lingo2-models';
import { takeUntil } from 'rxjs/operators';

interface ShareAndStatsChangesType {
  visit_info: Partial<IContentVisitInfo>;
  stats: Partial<IContentStats>;
}

type ShareAndStatsDisplayMode = 'default' | 'content_page';

@Component({
  selector: 'app-share-and-stats',
  templateUrl: './share-and-stats.component.html',
  styleUrls: ['./share-and-stats.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareAndStatsComponent extends ChangableComponent implements OnInit {
  @Input() content_id: string;
  @Input() stats: Partial<IContentStats>;
  @Input() visit_info: IContentVisitInfo;
  @Input() displayMode: ShareAndStatsDisplayMode = 'default';
  @Output() changed = new EventEmitter<ShareAndStatsChangesType>();
  @Output() shared = new EventEmitter<boolean>();
  public isAuth = this.authService.isAuthenticated;
  public url: string;

  constructor(
    public errorNotificationService: ErrorNotificationService,
    private authService: AuthService,
    private contentService: ContentService,
    private contextService: ContextService,
    private features: FeaturesService,
    protected cdr: ChangeDetectorRef,
  ) {
    super(cdr);
    this.url = window.location.href;
  }

  public ngOnInit(): void {
    this.contentService.change$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (res) => {
        if (res && res.id === this.content_id) {
          this.stats.views_count = res.stats.views_count;
          this.detectChanges();
        }
      },
      error: (error) => {
        this.errorNotificationService.captureError(error, 'OTHER-PROBLEM');
      },
    });
  }

  public like() {
    this.contentService
      .like(this.content_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const visit_info = {
            is_liked: response.like === LikeDislikeEnum.like,
            is_disliked: response.like === LikeDislikeEnum.dislike,
          };
          const stats = {
            likes_count: response.likes_count,
            dislikes_count: response.dislikes_count,
          };

          this.contentService.change.next({
            id: this.content_id,
            stats: { ...this.stats, ...stats },
          });
          this.changed.emit({ visit_info, stats });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public dislike() {
    this.contentService
      .dislike(this.content_id)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response) => {
          const visit_info = {
            is_liked: response.like === LikeDislikeEnum.like,
            is_disliked: response.like === LikeDislikeEnum.dislike,
          };
          const stats = {
            likes_count: response.likes_count,
            dislikes_count: response.dislikes_count,
          };
          this.changed.emit({ visit_info, stats });
          this.contentService.change.next({
            id: this.content_id,
            stats: { ...this.stats, ...stats },
          });
          this.detectChanges();
        },
        error: (error) => {
          this.errorNotificationService.captureError(error, 'LOAD-SOMEDATA');
        },
      });
  }

  public onCopyLink() {
    this.shared.emit(true);
  }

  public authorize() {
    this.authService.showAuthModal();
  }

  public get isAvailableStatistics() {
    return this.features.isAvailable(FeatureEnum.statistics);
  }
}
